import type { QueryClient } from "@tanstack/react-query";
import { autoDiscoverContact } from "@/lib/auto-discover.functions";
import { runContactScanStep, markEnrichmentPartial } from "@/lib/enrichment-steps.functions";
import { synthesizeNow } from "@/lib/opportunities.functions";

/**
 * Client-side discovery queue for multi-add.
 *
 * The single-add path runs discovery via <DiscoverOverlay> on the contact
 * detail page. Batch adds never land on a detail page, so without this queue
 * those contacts would sit un-enriched until a nightly job touched them.
 * Concurrency is capped so a 10-person paste doesn't hammer the scraper.
 */

export type QueueSnapshot = { total: number; done: number; running: boolean };

const CONCURRENCY = 2;

let total = 0;
let done = 0;
let active = 0;
const pending: string[] = [];
const listeners = new Set<(s: QueueSnapshot) => void>();
let qc: QueryClient | null = null;

function snapshot(): QueueSnapshot {
  return { total, done, running: active > 0 || pending.length > 0 };
}

function emit() {
  const s = snapshot();
  for (const l of listeners) l(s);
}

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot();
}

export function subscribeQueue(fn: (s: QueueSnapshot) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Lets the queue refresh caches even though it lives outside React. */
export function setEnrichmentQueryClient(client: QueryClient) {
  qc = client;
}

async function runOne(contactId: string) {
  // Two short requests instead of one long one: discovery resolves identity
  // and links, then the scan step reads pages, news, context and photo. If
  // either is dropped the person is settled as `partial`, never left "running".
  try {
    await autoDiscoverContact({ data: { contactId } });
    qc?.invalidateQueries({ queryKey: ["contact", contactId] });
    qc?.invalidateQueries({ queryKey: ["enrichment-status", contactId] });
    await runContactScanStep({ data: { contactId } });
  } catch {
    // Settle the row so the card stops claiming it's working. The scheduled
    // sweeper still resumes anything genuinely unfinished.
    try { await markEnrichmentPartial({ data: { contactId } }); } catch { /* sweeper handles it */ }
  } finally {
    done++;
    qc?.invalidateQueries({ queryKey: ["contact", contactId] });
    qc?.invalidateQueries({ queryKey: ["enrichment-status", contactId] });
    qc?.invalidateQueries({ queryKey: ["people"] });
    emit();
  }
}

function pump() {
  while (active < CONCURRENCY && pending.length) {
    const id = pending.shift()!;
    active++;
    runOne(id).finally(() => {
      active--;
      if (!pending.length && active === 0) {
        qc?.invalidateQueries({ queryKey: ["people"] });
        // The batch is enriched — build the feed now instead of leaving the
        // first Today empty until the nightly synthesis job runs.
        void synthesizeNow()
          .catch(() => null)
          .then(() => {
            qc?.invalidateQueries({ queryKey: ["opportunity-feed"] });
            qc?.invalidateQueries({ queryKey: ["home-digest"] });
          });
        // Reset counters once idle so the next batch starts from zero.
        total = 0;
        done = 0;
      }
      emit();
      pump();
    });
  }
  emit();
}

/** Enqueue discovery for freshly created contacts. Safe to call repeatedly. */
export function enqueueDiscovery(ids: string[]) {
  const fresh = ids.filter(Boolean);
  if (!fresh.length) return;
  total += fresh.length;
  pending.push(...fresh);
  pump();
}
